import { useToast } from '@/view/components/ui/use-toast'
import { useQueryClient } from '@tanstack/react-query'
import { updateTable } from '@/app/services/tableService/updateTable'
import { PartnerType } from '@/app/utils/partners.enum'
import { configService } from '@/app/services/configService'
import { TriStateEnum } from '@/app/entities/TriState'
import { z } from 'zod'

const formSchema = z.object({
  delay: z.string().optional(),
  valuesAndCommissions: z
    .array(
      z.object({
        valueFrom: z.string().optional(),
        value: z.string(),
        commission: z.string(),
        leaderCommission: z.string().optional(),
        limit: z.string().optional(),
        max: z.string().optional(),
      }),
    )
    .optional(),
})

type FormValues = z.infer<typeof formSchema>

interface UseEditPartnerControllerParams {
  partnerData?: {
    partner: string
    itens: { label: string | number; value: number }[]
    tableId?: string
    delay?: number
    isActive?: boolean
    state?: TriStateEnum
  } | null
}

export const useEditPartnerController = ({
  partnerData,
}: UseEditPartnerControllerParams) => {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const isCrefaz = partnerData?.partner === PartnerType.CREFAZ
  const isSalvcar = partnerData?.partner === PartnerType.SALVCAR
  const isPan = partnerData?.partner === PartnerType.PAN

  const handleSubmit = async (data: FormValues): Promise<{ invalidIndexes?: number[] } | void> => {
    if (!partnerData) return

    const items = data.valuesAndCommissions ?? []

    const isPan = partnerData.partner === PartnerType.PAN

    if (isCrefaz || isSalvcar) {
      const invalidIndexes: number[] = []
      
      items.forEach((item, index) => {
        if (!item.max) return

        const leaderCommission = Number(
          String(item.leaderCommission || '0').replace(/\D/g, ''),
        )
        const commission = Number(
          String(item.commission || '0').replace(/\D/g, ''),
        )
        const max = Number(String(item.max).replace(/\D/g, ''))

        if (leaderCommission + commission > max) {
          invalidIndexes.push(index)
        }
      })

      if (invalidIndexes.length > 0) {
        toast({
          title: 'Erro de validação',
          description:
            'A soma das comissões (Líder + Vendedor) não pode ultrapassar o máximo.',
          variant: 'destructive',
        })
        return { invalidIndexes }
      }
    }

    if (isPan && items.length > 0) {
      const first = items[0]
      const percents = Number(
        String(first.value).replace('%', '').replace(',', '.'),
      )
      const leaderCommission = Number(
        String(first.leaderCommission || '0').replace('%', '').replace(',', '.'),
      )
      const max = first.max
        ? Number(String(first.max).replace('%', '').replace(',', '.'))
        : undefined

      if (max !== undefined && percents + leaderCommission > max) {
        toast({
          title: 'Erro de validação',
          description:
            'A soma das comissões (Líder + Vendedor) não pode ultrapassar o máximo.',
          variant: 'destructive',
        })
        return { invalidIndexes: [0] }
      }
    }

    try {

      let payload: any = {
        name: partnerData.partner,
      }

      if (isPan) {
        const first = items[0]

        const percents = Number(
          String(first.value).replace('%', '').replace(',', '.'),
        )
        const bonus = Number(
          String(first.commission).replace('%', '').replace(',', '.'),
        )
        const leaderCommissionStr = String(first.leaderCommission || '0')
          .replace('%', '')
          .replace(',', '.')
          .trim()
        const leaderCommission = Number(leaderCommissionStr)
        const max = first.max
          ? Number(String(first.max).replace(/\D/g, '')) / 100
          : undefined

        payload = {
          name: partnerData.partner,
          percents,
          bonus,
          leaderCommission,
          ...(max !== undefined && { maxPercents: max }),
        }
      } else {
        const valuesAndCommissions = items.map((item) => {
          const value = Number(String(item.value).replace(/\D/g, ''))

          let commission: number
          if (isSalvcar) {
            commission = Number(String(item.commission).replace(/\D/g, ''))
          } else {
            const commissionStr = String(item.commission)
              .replace('%', '')
              .replace(',', '.')
              .trim()

            commission = Number(commissionStr)
          }

          let leaderCommission: number
          if (isSalvcar) {
            leaderCommission = Number(String(item.leaderCommission || '0').replace(/\D/g, ''))
          } else {
            const leaderCommissionStr = String(item.leaderCommission || '0')
              .replace('%', '')
              .replace(',', '.')
              .trim()
            leaderCommission = Number(leaderCommissionStr)
          }

          const limit = item.limit
            ? Number(String(item.limit).replace(/\D/g, ''))
            : undefined

          let max: number | undefined
          if (item.max) {
            if (isSalvcar) {
              max = Number(String(item.max).replace(/\D/g, ''))
            } else {
              max = Number(String(item.max).replace('%', '').replace(',', '.').trim())
            }
          }

          return {
            value,
            commission,
            ...(leaderCommission > 0 && { leaderCommission: leaderCommission }),
            ...(limit !== undefined && { limit }),
            ...(max !== undefined && { max }),
          }
        })

        payload = {
          name: partnerData.partner,
          percents: 6,
          valuesAndCommissions,
        }
      }

      if (!partnerData.tableId) throw new Error('Table ID não encontrado')
      await updateTable(payload, partnerData.tableId)

      if (data.delay !== undefined) {
        await configService.patchConfigProducts({
          type: partnerData.partner as PartnerType,
          delay: Number(data.delay),
          isActive: partnerData.isActive ?? false,
          state: partnerData.state ?? TriStateEnum.OFF,
        })
      }

      queryClient.invalidateQueries({ queryKey: ['productsConfig'] })

      toast({
        title: 'Sucesso!',
        description: 'Valores atualizados com sucesso.',
      })
    } catch (error) {
      console.error('Erro ao atualizar:', error)
      toast({
        title: 'Erro ao atualizar valores',
        description: 'Tente novamente mais tarde.',
        variant: 'destructive',
      })
    }
  }

  let initialValues: any[] = []

  if (partnerData?.partner === PartnerType.PAN) {
    const percentItem = partnerData.itens.find((i) => i.label === 'percents')
    const bonusItem = partnerData.itens.find((i) => i.label === 'bonus')
    const limitItem = partnerData.itens.find((i) => i.label === 'limit')
    const leaderCommissionItem = partnerData.itens.find((i) => i.label === 'leaderCommission')
    const maxItem = partnerData.itens.find((i) => i.label === 'max')

    initialValues = [
      {
        value: percentItem?.value ?? 0,
        commission: bonusItem?.value ?? 0,
        limit: limitItem?.value ?? 0,
        leaderCommission: leaderCommissionItem?.value ?? 0,
        max: maxItem?.value ?? 0,
      },
    ]
  } else {
    initialValues =
      partnerData?.itens.map((item) => ({
        value: Number(item.label),
        commission: item.value,
        leaderCommission: (item as any).leaderCommission,
        limit: (item as any).limit,
        max: (item as any).max,
      })) ?? []
  }

  return {
    handleSubmit,
    initialValues,
    isCrefaz,
    isSalvcar,
    isPan,
    partnerData,
    formSchema,
  }
}
